import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../context/AuthContext';
import authStorage from '../../../lib/authStorage';

const LoginForm = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState(() => ({
    email: '',
    password: '',
    rememberMe: Boolean(authStorage.getRememberMeToken()),
  }));
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const newErrors = validateForm();
    
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsLoading(true);
    setErrors({});
    try {
      const profile = await login({
        email: formData?.email,
        password: formData?.password,
        rememberMe: formData?.rememberMe,
      });

      if (formData?.rememberMe) {
        localStorage.setItem(
          'rememberedEmail',
          JSON.stringify({ email: formData?.email }),
        );
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      const role = typeof profile?.role === 'string' ? profile.role.toLowerCase() : '';

      if (role === 'employee') {
        navigate('/employee-self-service', { replace: true });
      } else if (role === 'manager' || role === 'admin') {
        navigate('/manager-self-service', { replace: true });
      } else {
        navigate('/employee-scheduling', { replace: true });
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        'Invalid email or password. Please try again.';
      setErrors({
        general: Array.isArray(message) ? message.join('. ') : message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password', { state: { email: formData?.email } });
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* General Error Message */}
        {errors?.general && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start space-x-3">
            <Icon name="AlertCircle" size={20} className="text-error mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-error font-medium">Authentication Failed</p>
              <p className="text-sm text-error/80 mt-1">{errors?.general}</p>
            </div>
          </div>
        )}

        {/* Email Input */}
        <Input
          label="Email Address"
          type="email"
          name="email"
          placeholder="Enter your work email"
          value={formData?.email}
          onChange={handleInputChange}
          error={errors?.email}
          required
          disabled={isLoading}
        />

        {/* Password Input */}
        <Input
          label="Password"
          type="password"
          name="password"
          placeholder="Enter your password"
          value={formData?.password}
          onChange={handleInputChange}
          error={errors?.password}
          required
          disabled={isLoading}
        />

        {/* Remember Me Checkbox */}
        <div className="flex items-center justify-between">
          <Checkbox
            label="Remember me"
            name="rememberMe"
            checked={formData?.rememberMe}
            onChange={handleInputChange}
            disabled={isLoading}
          />
          
          <button
            type="button"
            onClick={handleForgotPassword}
            className="text-sm text-primary hover:text-primary/80 transition-colors font-medium"
            disabled={isLoading}
          >
            Forgot password?
          </button>
        </div>

        {/* Sign In Button */}
      <Button
        type="submit"
        variant="default"
        loading={isLoading}
        fullWidth
        iconName="LogIn"
        iconPosition="right"
        className="mt-8"
      >
        {isLoading ? 'Signing In...' : 'Sign In'}
      </Button>
    </form>

    <div className="mt-6 text-center text-xs text-muted-foreground">
      Invited to join? Use the onboarding link in your email to finish setting up your account.
    </div>
  </div>
);
};

export default LoginForm;
